import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Activity, CloudOff, History } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db } from "../lib/db";
import { Card, EmptyState, InlineNotice, PageHeader, StatusBadge } from "../components/ui";
import { formatDateTime } from "../lib/format";

interface ActivityItem {
  id: string;
  actorUserId: string;
  actorName?: string;
  action: string;
  entityType: string;
  entityId: string;
  reason?: string | null;
  createdAt: string;
}

const actionLabels: Record<string, string> = {
  SALE_COMPLETED: "Hoàn tất bán hàng",
  STOCK_ADJUSTED: "Điều chỉnh tồn kho",
  DAILY_CLOSED: "Đóng sổ ngày",
  DEBT_COLLECTED: "Thu công nợ",
  DEBT_PAID: "Trả công nợ nhà cung cấp",
};

export function ActivityPage() {
  const auth = useAuth();
  const cached = useLiveQuery(async () => ((await db.meta.get("activity"))?.value as ActivityItem[] | undefined) ?? [], [], []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auth.demo || !navigator.onLine) return;
    setLoading(true);
    void api<ActivityItem[]>("/activity")
      .then(async (items) => {
        await db.meta.put({ key: "activity", value: items });
      })
      .finally(() => setLoading(false));
  }, [auth.demo]);

  const pending = useLiveQuery(
    () => db.syncQueue.where("status").anyOf("PENDING", "FAILED", "SYNCING").toArray(),
    [],
    [],
  );

  return (
    <div className="page-stack">
      <PageHeader back title="Lịch sử hoạt động" description="Các thay đổi quan trọng được giữ lại để có thể truy ngược" />

      {!navigator.onLine ? (
        <InlineNotice tone="warning" title="Đang xem dữ liệu đã lưu gần nhất">
          <span><CloudOff size={16} /> Những thay đổi chưa đồng bộ sẽ hiển thị riêng bên dưới.</span>
        </InlineNotice>
      ) : null}

      {pending.length > 0 ? (
        <section>
          <div className="section-title"><div><Activity size={20} /><h2>Chờ đồng bộ</h2></div><StatusBadge tone="warning">{pending.length}</StatusBadge></div>
          <Card className="activity-list">
            {pending.map((item) => (
              <div className="activity-row" key={item.id}>
                <span className="activity-icon"><History size={18} /></span>
                <div><strong>{item.type}</strong><small>{formatDateTime(item.createdAt)} · {item.status === "FAILED" ? "Có lỗi đồng bộ" : "Chờ đồng bộ"}</small></div>
                <StatusBadge tone={item.status === "FAILED" ? "danger" : "info"}>{item.status === "FAILED" ? "Lỗi" : "Cục bộ"}</StatusBadge>
              </div>
            ))}
          </Card>
        </section>
      ) : null}

      <section>
        <div className="section-title"><div><History size={20} /><h2>Đã ghi nhận</h2></div>{loading ? <span>Đang cập nhật...</span> : null}</div>
        {cached.length === 0 ? (
          <EmptyState title="Chưa có lịch sử" description="Các thao tác như bán hàng, điều chỉnh tồn và đóng sổ sẽ xuất hiện ở đây." />
        ) : (
          <Card className="activity-list">
            {cached.map((item) => (
              <div className="activity-row" key={item.id}>
                <span className="activity-icon"><History size={18} /></span>
                <div>
                  <strong>{actionLabels[item.action] ?? item.action}</strong>
                  <small>{item.actorName ?? "Người dùng"} · {formatDateTime(item.createdAt)}{item.reason ? ` · ${item.reason}` : ""}</small>
                </div>
                <StatusBadge tone="neutral">{item.entityType}</StatusBadge>
              </div>
            ))}
          </Card>
        )}
      </section>
    </div>
  );
}
